// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Disposal Sites CRUD
// TDSR, DMS, final disposal, recycler management
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Autocomplete,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect, Progress,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisDisposalSite, SiteStatus } from '../../types/debris';
import { WORKER_URL } from '../../lib/api-switch';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'full', label: 'Full' },
  { value: 'closed', label: 'Closed' },
];

const SITE_TYPES = [
  { value: 'tdsr', label: 'TDSR (Temporary Staging)' },
  { value: 'dms', label: 'DMS (Debris Management)' },
  { value: 'final_disposal', label: 'Final Disposal' },
  { value: 'recycler', label: 'Recycler' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'green', full: 'orange', closed: 'gray',
};

const DEBRIS_TYPE_OPTIONS = Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

const EMPTY: Partial<DebrisDisposalSite> = {
  siteName: '', siteType: '', address: '', latitude: '', longitude: '',
  capacityCubicYards: '', currentVolume: '0', acceptedTypes: '[]',
  operatingHours: '', monitorName: '', monitorEmail: '',
  status: 'open', notes: '',
};

export default function DisposalSites() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    disposalSites, loadingSites,
    loadDisposalSites, createDisposalSite, updateDisposalSite, deleteDisposalSite,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisDisposalSite | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisDisposalSite | null>(null);
  const [form, setForm] = useState<Partial<DebrisDisposalSite>>({ ...EMPTY });
  const [formTypes, setFormTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<{ value: string; lat: string; lng: string }[]>([]);
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setField('address', q);
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrSuggestions((data.items || []).filter((i: any) => i.position).map((i: any) => ({
          value: i.title || i.address?.label || q,
          lat: String(i.position.lat), lng: String(i.position.lng),
        })));
      } catch { /* ignore */ }
    }, 300);
  };

  useEffect(() => { loadDisposalSites(); }, [loadDisposalSites]);

  const iSites = incidentId ? disposalSites : [];

  // Build monitor dropdown from existing sites
  const knownMonitors = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    disposalSites.forEach(s => {
      if (s.monitorName?.trim()) map.set(s.monitorName, { name: s.monitorName, email: s.monitorEmail || '' });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [disposalSites]);
  const monitorSelectData = useMemo(() => knownMonitors.map(m => ({ value: m.name, label: m.name })), [knownMonitors]);

  const filtered = useMemo(() => {
    let list = iSites;
    if (statusFilter && statusFilter !== 'all') list = list.filter(s => s.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.siteName.toLowerCase().includes(q) || s.address.toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.siteName.localeCompare(b.siteName));
  }, [iSites, statusFilter, search]);

  const setField = (key: keyof DebrisDisposalSite, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setFormTypes([]); setModalOpen(true); };
  const openEdit = (s: DebrisDisposalSite) => {
    setEditTarget(s);
    setForm({ ...s });
    try { setFormTypes(JSON.parse(s.acceptedTypes || '[]')); } catch { setFormTypes([]); }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.siteName?.trim()) {
      notifications.show({ message: 'Site name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId, acceptedTypes: JSON.stringify(formTypes) };
      if (editTarget) {
        await updateDisposalSite(editTarget.id, payload);
        notifications.show({ message: 'Site updated', color: 'green' });
      } else {
        await createDisposalSite(payload);
        notifications.show({ message: 'Site created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDisposalSite(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.siteName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Disposal Sites</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingSites} onClick={() => loadDisposalSites()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Site</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search site name, address..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Site Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Address</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Capacity</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', minWidth: 120 }}>Fill Level</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Monitor</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingSites ? 'Loading...' : 'No disposal sites found'}
                </td></tr>
              )}
              {filtered.map(s => {
                const cap = parseFloat(s.capacityCubicYards || '0');
                const vol = parseFloat(s.currentVolume || '0');
                const pct = cap > 0 ? Math.min((vol / cap) * 100, 100) : 0;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{s.siteName || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" variant="light">
                        {SITE_TYPES.find(st => st.value === s.siteType)?.label ?? s.siteType ?? '-'}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{s.address || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={STATUS_COLORS[s.status] ?? 'gray'}>{s.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {cap > 0 ? `${cap.toLocaleString()} CY` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {cap > 0 ? (
                        <Group gap={4}>
                          <Progress value={pct} size="sm" style={{ flex: 1 }}
                            color={pct >= 90 ? 'red' : pct >= 70 ? 'orange' : 'green'} />
                          <Text size="xs" w={40} ta="right">{pct.toFixed(0)}%</Text>
                        </Group>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Text size="sm">{s.monitorName || '-'}</Text>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <Group gap={4} justify="flex-end">
                        {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                          <IconPencil size={14} />
                        </ActionIcon>}
                        {canEdit && <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(s)}>
                          <IconTrash size={14} />
                        </ActionIcon>}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Disposal Site' : 'Add Disposal Site'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Site Name" required value={form.siteName ?? ''}
              onChange={e => setField('siteName', e.currentTarget.value)} />
            <Select label="Site Type" data={SITE_TYPES} value={form.siteType || null}
              onChange={v => setField('siteType', v ?? '')} clearable />
            <Autocomplete label="Address" value={form.address ?? ''}
              onChange={handleAddrSearch}
              onOptionSubmit={val => {
                setField('address', val);
                const match = addrSuggestions.find(s => s.value === val);
                if (match) { setField('latitude', match.lat); setField('longitude', match.lng); }
              }}
              data={addrSuggestions.map(s => s.value)}
              placeholder="Start typing an address..." />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as SiteStatus) ?? 'open'}
              onChange={v => setField('status', v ?? 'open')} />
            <TextInput label="Latitude" value={form.latitude ?? ''}
              onChange={e => setField('latitude', e.currentTarget.value)} />
            <TextInput label="Longitude" value={form.longitude ?? ''}
              onChange={e => setField('longitude', e.currentTarget.value)} />
            <TextInput label="Capacity (CY)" value={form.capacityCubicYards ?? ''}
              onChange={e => setField('capacityCubicYards', e.currentTarget.value)} />
            <TextInput label="Current Volume (CY)" value={form.currentVolume ?? ''}
              onChange={e => setField('currentVolume', e.currentTarget.value)} />
            <TextInput label="Operating Hours" value={form.operatingHours ?? ''}
              onChange={e => setField('operatingHours', e.currentTarget.value)} placeholder="e.g. 7AM-5PM Mon-Sat" />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Tower Monitor</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Monitor Name" data={monitorSelectData} value={form.monitorName || null}
              onChange={v => {
                setField('monitorName', v ?? '');
                const m = knownMonitors.find(m => m.name === v);
                if (m) setField('monitorEmail', m.email);
              }}
              searchable clearable allowDeselect
              nothingFoundMessage="Type a new monitor name above"
            />
            <TextInput label="Monitor Email" value={form.monitorEmail ?? ''}
              onChange={e => setField('monitorEmail', e.currentTarget.value)} />
          </SimpleGrid>

          <MultiSelect label="Accepted Debris Types" data={DEBRIS_TYPE_OPTIONS}
            value={formTypes} onChange={setFormTypes} searchable />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Site'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Site" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.siteName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
